import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowLeft, Lock, MapPin, Plus, Trash2, Check, Loader2, CalendarDays, ImagePlus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { SERVICE_AREAS, DELIVERY_NOTE, SERVICEABLE_PINCODES } from "../mock";
import { createOrder, getProfile, addAddress, deleteAddress, fetchBlockedDates, uploadCustomCakeImage } from "../lib/api";
import { WhatsAppIcon } from "../components/WhatsAppButton";
import { inr } from "../lib/format";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromISO = (s) => { const [y, m, dd] = s.split("-").map(Number); return new Date(y, m - 1, dd); };

const ADMIN_WA = "919264971235";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Checkout = () => {
  const { items, subtotal, clearCart, lineKey, updateItem } = useCart();
  const { user, openLogin } = useAuth();
  const navigate = useNavigate();
  const [placed, setPlaced] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [placedItems, setPlacedItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Scheduling window: from next day up to one month ahead.
  const TOMORROW_ISO = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  const ONE_MONTH = new Date();
  ONE_MONTH.setMonth(ONE_MONTH.getMonth() + 1);
  const MAX_ISO = ONE_MONTH.toISOString().split("T")[0];
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", city: SERVICE_AREAS[0], pincode: "", date: TOMORROW_ISO, slot: "Anytime (9AM-9PM)" });
  const [addresses, setAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [showNewAddr, setShowNewAddr] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [dateOpen, setDateOpen] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const handleRefUpload = async (e, key) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadCustomCakeImage(fd);
      updateItem(key, { ref_image: `${BACKEND}${res.url}` });
    } catch (err) {
      setError(err.response?.data?.detail || "Could not upload the reference photo. Please try again.");
    } finally {
      setUploadingKey(null);
    }
  };

  useEffect(() => { fetchBlockedDates().then(setBlockedDates).catch(() => {}); }, []);
  const disabledDays = [{ before: fromISO(TOMORROW_ISO) }, { after: fromISO(MAX_ISO) }, ...blockedDates.map(fromISO)];

  const applyAddress = (a) => {
    // Only set the delivery location fields; keep the account name/phone intact.
    setForm((f) => ({
      ...f,
      address: a.address || "",
      city: SERVICE_AREAS.includes(a.city) ? a.city : f.city,
      pincode: a.pincode || "",
    }));
  };

  useEffect(() => {
    if (!user) return;
    getProfile()
      .then((p) => {
        setForm((f) => ({
          ...f,
          name: f.name || p.name || user.name || "",
          email: f.email || p.email || "",
          phone: f.phone || user.phone || p.phone || "",
        }));
        const list = p.addresses || [];
        setAddresses(list);
        if (list.length > 0) {
          setSelectedAddrId(list[0].id);
          applyAddress(list[0]);
          setShowNewAddr(false);
        } else {
          setShowNewAddr(true);
        }
      })
      .catch(() => {
        setForm((f) => ({ ...f, name: f.name || user.name || "", phone: f.phone || user.phone || "" }));
        setShowNewAddr(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const selectSaved = (a) => {
    setSelectedAddrId(a.id);
    setShowNewAddr(false);
    applyAddress(a);
  };

  const startNewAddress = () => {
    setSelectedAddrId(null);
    setShowNewAddr(true);
    setForm((f) => ({ ...f, address: "", pincode: "", city: SERVICE_AREAS[0] }));
  };

  const removeAddress = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Remove this saved address?")) return;
    try {
      const res = await deleteAddress(id);
      setAddresses(res.addresses || []);
      if (selectedAddrId === id) {
        if ((res.addresses || []).length > 0) selectSaved(res.addresses[0]);
        else startNewAddress();
      }
    } catch {
      setError("Could not remove the address. Please try again.");
    }
  };

  const delivery = subtotal >= 999 || subtotal === 0 ? 0 : 49;
  const total = subtotal + delivery;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pincodeEntered = form.pincode.length === 6;
  const inZone = pincodeEntered && SERVICEABLE_PINCODES.has(form.pincode);
  const dateBlocked = form.date && blockedDates.includes(form.date);
  const valid = form.name && form.phone.length >= 10 && form.address && form.pincode && form.date && !dateBlocked && SERVICE_AREAS.includes(form.city) && inZone;

  const [savingAddr, setSavingAddr] = useState(false);
  const saveCurrentAddress = async () => {
    setError("");
    if (!form.address || form.pincode.length !== 6 || !inZone) {
      setError("Enter a valid in-zone delivery address and pincode to save it.");
      return;
    }
    setSavingAddr(true);
    try {
      const res = await addAddress({ label: form.city, name: form.name, phone: form.phone, address: form.address, city: form.city, pincode: form.pincode });
      setAddresses(res.addresses || []);
      if (res.added?.id) setSelectedAddrId(res.added.id);
      setShowNewAddr(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save the address.");
    } finally {
      setSavingAddr(false);
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      // Persist a newly entered address to the customer's address book for next time.
      if (showNewAddr && user) {
        try {
          const res = await addAddress({ label: form.city, name: form.name, phone: form.phone, address: form.address, city: form.city, pincode: form.pincode });
          setAddresses(res.addresses || []);
        } catch {}
      }
      const payload = {
        ...form,
        items: items.map((i) => ({ id: i.id, name: i.name, image: i.image, price: i.price, flavor: i.flavor, weight: i.weight, qty: i.qty, message: i.message || "", ref_image: i.ref_image || "" })),
        subtotal, delivery_fee: delivery, total,
      };
      const order = await createOrder(payload);
      setPlacedItems(items);
      setOrderId(order.id);
      setPlaced(true);
      clearCart();
      window.scrollTo(0, 0);
    } catch (err) {
      setError("Something went wrong placing your order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappOrderUrl = () => {
    const lines = placedItems.map((i) => `• ${i.name} (${i.flavor}, ${i.weight}) x${i.qty} — ${inr(i.price * i.qty)}`).join("\n");
    const msg = `New Order ${orderId}\n\nCustomer: ${form.name}\nPhone: ${form.phone}\nDeliver: ${form.date} (${form.slot})\nAddress: ${form.address}, ${form.city} - ${form.pincode}\n\nItems:\n${lines}\n\nTotal: ${inr(total)}`;
    return `https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(msg)}`;
  };

  if (placed) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="w-20 h-20 rounded-full bg-[#E7F3E0] grid place-items-center mx-auto mb-6"><CheckCircle2 size={44} className="text-[#6BA84F]" /></div>
        <h1 className="font-display text-3xl font-bold text-[#3A2417]">Order Confirmed!</h1>
        <p className="text-[#6B4226] mt-3">Thank you, {form.name.split(" ")[0]}! Your cake is being freshly baked with love.</p>
        <div className="bg-[#FCEFE3] rounded-2xl p-5 mt-6 inline-block">
          <div className="text-sm text-[#B07A5C]">Order ID</div>
          <div className="font-display text-2xl font-bold text-[#D46A8A]">{orderId}</div>
        </div>
        <p className="text-sm text-[#6B4226] mt-4">We've notified the Globaltreats team. You can also share your order on WhatsApp:</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
          <a href={whatsappOrderUrl()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[#25D366] text-white font-medium hover:bg-[#1eb956] transition-colors">
            <WhatsAppIcon size={20} /> Send order to WhatsApp
          </a>
          <Link to="/collections/all" className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-[#D46A8A] text-white font-medium hover:bg-[#c25a7b] transition-colors">Continue Shopping</Link>
        </div>
        <p className="text-xs text-[#B07A5C] mt-5">(This is an MVP demo order — no real payment was processed.)</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="checkout-login-gate">
        <div className="w-20 h-20 rounded-full bg-[#25D366]/15 grid place-items-center mx-auto mb-6"><WhatsAppIcon size={38} /></div>
        <h1 className="font-display text-3xl font-bold text-[#3A2417]">Login to place your order</h1>
        <p className="text-[#B07A5C] mt-3">Verify your number with a quick WhatsApp OTP to continue to checkout and track your order later.</p>
        <button onClick={() => openLogin("/checkout")} className="mt-6 h-12 px-8 rounded-full bg-[#25D366] text-white font-medium hover:bg-[#1eb956] transition-colors" data-testid="checkout-login-btn">
          Login with WhatsApp
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <p className="text-[#6B4226] text-lg">Your cart is empty.</p>
        <Link to="/collections/all" className="inline-block mt-4 text-[#D46A8A]">Browse cakes</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-[#B07A5C] hover:text-[#D46A8A] mb-6"><ArrowLeft size={16} /> Back</button>
      <h1 className="font-display text-3xl font-bold text-[#3A2417] mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-8">
        {/* Form */}
        <form onSubmit={placeOrder} className="bg-white rounded-2xl p-6 bakery-shadow space-y-5">
          <h2 className="font-display text-xl font-semibold text-[#3A2417]">Delivery Details</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full Name" value={form.name} onChange={(v) => update("name", v)} required testId="checkout-name-input" />
            <Field label="Phone Number" value={form.phone} onChange={(v) => update("phone", v.replace(/\D/g, ""))} required type="tel" testId="checkout-phone-input" />
          </div>
          <Field label="Email" value={form.email} onChange={(v) => update("email", v)} type="email" testId="checkout-email-input" />

          {/* Address book */}
          <div className="space-y-3" data-testid="checkout-address-book">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#3A2417]">Delivery Location</h3>
              {addresses.length > 0 && (
                <button type="button" onClick={startNewAddress} className="inline-flex items-center gap-1 text-sm text-[#D46A8A] hover:underline" data-testid="checkout-add-address-btn">
                  <Plus size={15} /> Add new address
                </button>
              )}
            </div>
            {addresses.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-3">
                {addresses.map((a) => (
                  <div
                    key={a.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectSaved(a)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectSaved(a); } }}
                    className={`relative cursor-pointer text-left rounded-xl border p-3 transition-colors ${selectedAddrId === a.id ? "border-[#D46A8A] bg-[#FCEFE3]" : "border-[#EADBCB] bg-white hover:border-[#E8A6B8]"}`}
                    data-testid={`checkout-saved-address-${a.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-[#6B4226] bg-white/70 border border-[#EADBCB] rounded-full px-2 py-0.5">{a.label || a.city}</span>
                      <span className="flex items-center gap-2">
                        {selectedAddrId === a.id && <Check size={15} className="text-[#D46A8A]" />}
                        <button type="button" onClick={(e) => removeAddress(e, a.id)} aria-label="Remove address" className="grid place-items-center w-6 h-6 rounded-full hover:bg-red-50" data-testid={`checkout-delete-address-${a.id}`}>
                          <Trash2 size={14} className="text-[#B07A5C] hover:text-red-500" />
                        </button>
                      </span>
                    </div>
                    <div className="text-sm text-[#3A2417] mt-1.5 line-clamp-2" title={a.address}>{a.address}</div>
                    <div className="text-xs text-[#B07A5C] mt-0.5">{a.city} - {a.pincode}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {(showNewAddr || addresses.length === 0) && (
            <div className="space-y-4 rounded-xl border border-dashed border-[#EADBCB] p-4" data-testid="checkout-new-address-form">
              {addresses.length > 0 && (
                <button type="button" onClick={() => selectSaved(addresses[0])} className="text-xs text-[#B07A5C] hover:underline inline-flex items-center gap-1">
                  <ArrowLeft size={12} /> Use a saved address
                </button>
              )}
              <Field label="Delivery Address" value={form.address} onChange={(v) => update("address", v)} required testId="checkout-address-input" />
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#6B4226]">City / Area<span className="text-[#D46A8A]"> *</span></label>
                  <select value={form.city} onChange={(e) => update("city", e.target.value)} className="w-full mt-1.5 h-11 px-3 rounded-xl bg-white border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A]">
                    {SERVICE_AREAS.map((a) => <option key={a}>{a}</option>)}
                  </select>
                </div>
                <Field label="Pincode" value={form.pincode} onChange={(v) => update("pincode", v.replace(/\D/g, ""))} required testId="checkout-pincode-input" />
              </div>
              {user && <p className="text-xs text-[#B07A5C]">This address will be saved to your account for faster checkout next time.</p>}
              {user && (
                <button type="button" onClick={saveCurrentAddress} disabled={savingAddr} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#FCEFE3] text-[#6B4226] text-sm hover:bg-[#f6e0cf] disabled:opacity-60" data-testid="checkout-save-address-btn">
                  {savingAddr ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Save this address
                </button>
              )}
            </div>
          )}
          {pincodeEntered ? (
            inZone ? (
              <div className="bg-[#EAF3E4] rounded-xl p-4 text-sm text-[#3f6b2e] flex items-start gap-2" data-testid="delivery-zone-ok">
                <span className="text-lg leading-none">🎉</span>
                <span><strong>Congratulations! We deliver to your area.</strong> Your pincode {form.pincode} is within our delivery zone.</span>
              </div>
            ) : (
              <div className="bg-[#FBE7E7] rounded-xl p-4 text-sm text-[#B03636] flex items-start gap-2" data-testid="delivery-zone-blocked">
                <MapPin size={18} className="text-[#C0392B] mt-0.5 shrink-0" />
                <span>Sorry, we don't deliver beyond 20 km of Sector 78, Noida. We currently serve Noida, Greater Noida & Indirapuram (Ghaziabad). Please check your pincode.</span>
              </div>
            )
          ) : (
            <div className="bg-[#EAF3E4] rounded-xl p-4 text-sm text-[#3f6b2e] flex items-start gap-2">
              <MapPin size={18} className="text-[#6BA84F] mt-0.5 shrink-0" />
              <span>{DELIVERY_NOTE}</span>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#6B4226]">Delivery Date<span className="text-[#D46A8A]"> *</span></label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="checkout-date-trigger" className="w-full mt-1.5 h-11 px-4 rounded-xl bg-white border border-[#EADBCB] text-sm text-left flex items-center gap-2 hover:border-[#D46A8A] outline-none focus:border-[#D46A8A] transition-colors">
                    <CalendarDays size={16} className="text-[#B07A5C]" />
                    <span className={form.date ? "text-[#3A2417]" : "text-[#B07A5C]"}>
                      {form.date ? fromISO(form.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "Select a date"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" data-testid="checkout-calendar">
                  <Calendar
                    mode="single"
                    selected={form.date ? fromISO(form.date) : undefined}
                    onSelect={(d) => { if (d) { update("date", toISO(d)); setDateOpen(false); } }}
                    disabled={disabledDays}
                    defaultMonth={form.date ? fromISO(form.date) : fromISO(TOMORROW_ISO)}
                    fromDate={fromISO(TOMORROW_ISO)}
                    toDate={fromISO(MAX_ISO)}
                    modifiers={{ blocked: blockedDates.map(fromISO) }}
                    modifiersClassNames={{ blocked: "line-through text-red-400" }}
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-[#B07A5C] mt-1">Next day onwards, up to 1 month. Holidays are greyed out.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-[#6B4226]">Delivery Slot</label>
              <select value={form.slot} onChange={(e) => update("slot", e.target.value)} className="w-full mt-1.5 h-11 px-3 rounded-xl bg-white border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A]">
                {["Anytime (9AM-9PM)", "Morning (9AM-12PM)", "Afternoon (12PM-4PM)", "Evening (4PM-9PM)"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="bg-[#FCEFE3] rounded-xl p-4 text-sm text-[#6B4226]">
            <div className="flex items-center gap-2 font-medium"><Lock size={16} className="text-[#D46A8A]" /> Payment (Demo)</div>
            <p className="text-xs text-[#B07A5C] mt-1">This is an MVP demo. Order is placed without real payment. A gateway (Razorpay/Stripe) can be added later.</p>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button type="submit" disabled={!valid || submitting} data-testid="checkout-place-order-btn" className={`w-full h-12 rounded-full font-medium transition-colors ${valid && !submitting ? "bg-[#D46A8A] text-white hover:bg-[#c25a7b]" : "bg-[#EADBCB] text-[#B07A5C] cursor-not-allowed"}`}>{submitting ? "Placing Order..." : `Place Order · ${inr(total)}`}</button>
        </form>

        {/* Summary */}
        <div className="bg-white rounded-2xl p-6 bakery-shadow h-fit lg:sticky lg:top-40">
          <h2 className="font-display text-xl font-semibold text-[#3A2417] mb-4">Order Summary</h2>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {items.map((i) => (
              <div key={lineKey(i)} className="flex gap-3" data-testid={`summary-item-${i.id}`}>
                <img src={i.image} alt={i.name} className="w-14 h-14 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#3A2417] line-clamp-1">{i.name}</div>
                  <div className="text-xs text-[#B07A5C]">{i.flavor} · {i.weight} × {i.qty}</div>
                  {i.ref_image ? (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={i.ref_image} alt="reference" className="w-10 h-10 rounded-lg object-cover border border-[#EADBCB]" data-testid={`ref-thumb-${i.id}`} />
                      <button type="button" onClick={() => updateItem(lineKey(i), { ref_image: "" })} className="text-xs text-[#B07A5C] hover:text-red-500" data-testid={`ref-remove-${i.id}`}>Remove photo</button>
                    </div>
                  ) : (
                    <label className={`mt-2 inline-flex items-center gap-1 text-xs text-[#D46A8A] cursor-pointer hover:underline ${uploadingKey === lineKey(i) ? "opacity-60 pointer-events-none" : ""}`} data-testid={`ref-add-${i.id}`}>
                      {uploadingKey === lineKey(i) ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                      {uploadingKey === lineKey(i) ? "Uploading..." : "Add reference photo"}
                      <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => handleRefUpload(e, lineKey(i))} />
                    </label>
                  )}
                </div>
                <div className="text-sm font-medium text-[#3A2417]">{inr(i.price * i.qty)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-[#EADBCB] mt-4 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-[#6B4226]"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
            <div className="flex justify-between text-[#6B4226]"><span>Delivery</span><span>{delivery === 0 ? "FREE" : inr(delivery)}</span></div>
            <div className="flex justify-between font-display text-lg font-bold text-[#3A2417] pt-2"><span>Total</span><span>{inr(total)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, required, type = "text", testId, min, max, hint, error }) => (
  <div>
    <label className="text-sm font-medium text-[#6B4226]">{label}{required && <span className="text-[#D46A8A]"> *</span>}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} min={min} max={max}
      data-testid={testId}
      className={`w-full mt-1.5 h-11 px-4 rounded-xl bg-white border text-sm outline-none focus:border-[#D46A8A] transition-colors ${error ? "border-red-400" : "border-[#EADBCB]"}`} />
    {error ? <p className="text-xs text-red-600 mt-1" data-testid={testId ? `${testId}-error` : undefined}>{error}</p> : hint ? <p className="text-xs text-[#B07A5C] mt-1">{hint}</p> : null}
  </div>
);

export default Checkout;
